/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useTimeGroupList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Time Groups list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchTimeGroups } from '../api/timeGroupApi';
import { TimeGroup } from '../types';

interface UseTimeGroupListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Time Groups list
 * Implements Domain Contextual Filtering pattern
 */
export function useTimeGroupList(options: UseTimeGroupListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchTimeGroupsData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchTimeGroups(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId,
          },
          params.search,
          params.sortBy,
        );

        return {
          data: result.data as TimeGroup[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('timeGroups:messages.loadError');
        showError(message);
        return {
          data: [] as TimeGroup[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId],
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('timeGroups.fields.name.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'description',
        label: t('timeGroups.fields.description.label'),
        visible: true,
        sortable: false,
      },
      {
        key: 'schedules',
        label: t('timeGroups.fields.schedules.label'),
        visible: true,
        sortable: false,
      },
      {
        key: 'enabled',
        label: t('timeGroups.fields.enabled.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'createdAt',
        label: t('common:createdAt'),
        visible: false,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchTimeGroupsData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as TimeGroup[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

